import React from 'react'
import { useQueryClient } from 'react-query'
import {
  SpiElementsProvider,
  useSpi,
  SpiPaymentElement
} from '@local/react-spi-js'
import { loadSpi } from '@local/spi-js'
import { Skeleton } from '@toasttab/buffet-pui-loading-indicators'
import { RestaurantInfo } from '../../api/restaurant/getRestaurant'
import { useFetchRestaurantV2 } from './api/useFetchRestaurantV2'
import { useCreditCardForm } from './CreditCardFormProvider'
import { payDepositSpi } from './utilities/api'
import { getSpiEnv } from './utilities/spi-helpers'
import {
  rootBearerTokenQueryKey,
  useFetchBearerToken
} from './api/useFetchBearerToken'
import {
  rootPaymentIntentQueryKey,
  useCreatePaymentIntent
} from './api/useCreatePaymentIntent'
import { track } from '@toasttab/do-secundo-analytics'
import { AnalyticsEvents } from '../../utils/analyticsEvents'
import { useSentry } from 'banquet-runtime-modules'
import { PaymentSuccessEvent } from './CreditCardForm'
import { Currency } from '@toasttab/buffet-pui-country-utilities'

const CreditCardFormDataPending = () => {
  return (
    <div className='flex flex-col rounded-lg gap-1 w-full'>
      <Skeleton className='h-12 w-full' />
      <Skeleton className='h-12 w-full' />
      <Skeleton className='h-12 w-full' />
    </div>
  )
}

export function CreditCardFormWithSpi(props: {
  email: string
  restaurant: RestaurantInfo
  onSuccess: PaymentSuccessEvent
  checkGuid: string
  orderGuid: string
  paymentAmount: number
}) {
  const [spiSdkPromise] = React.useState(() =>
    loadSpi(getSpiEnv(window.location.hostname))
  )
  // Fetch these things eagerly, do not wait for spi to load
  useFetchBearerToken()
  useCreatePaymentIntent({
    checkGuid: props.checkGuid,
    orderGuid: props.orderGuid,
    restaurantGuid: props.restaurant.guid
  })

  const queryClient = useQueryClient()
  React.useEffect(() => {
    // upon leave (due to cancellation or payment completion), invalidate SPI bearer token + payment intents so we start fresh
    return () => {
      queryClient
        .invalidateQueries([rootPaymentIntentQueryKey], {
          // Ensures that we don't unnecessarily refire the queries active on this page
          refetchActive: false,
          refetchInactive: false
        })
        .catch(() => {
          // swallow errors
        })

      queryClient
        .invalidateQueries([rootBearerTokenQueryKey], {
          // Ensures that we don't unnecessarily refire the queries active on this page
          refetchActive: false,
          refetchInactive: false
        })
        .catch(() => {
          // swallow errors
        })
    }
  }, [queryClient])

  return (
    <SpiElementsProvider spiPromise={spiSdkPromise}>
      <CreditCardFormWithSpiInner {...props} />
    </SpiElementsProvider>
  )
}

function CreditCardFormWithSpiInner(props: {
  email: string
  restaurant: RestaurantInfo
  onSuccess: PaymentSuccessEvent
  checkGuid: string
  orderGuid: string
  paymentAmount: number
}) {
  const sentry = useSentry()
  const { email, restaurant, onSuccess, checkGuid, orderGuid } = props
  const bearerTokenResponse = useFetchBearerToken()
  const paymentIntentResponse = useCreatePaymentIntent({
    checkGuid: checkGuid,
    orderGuid: orderGuid,
    restaurantGuid: restaurant.guid
  })
  const fetchRestaurantV2Response = useFetchRestaurantV2(restaurant.guid)
  const { spiSdk } = useSpi()
  const {
    setPaymentLoading,
    setPaymentError,
    setIsCcDataValid,
    selectedPaymentMethod,
    setSelectedPaymentMethod
  } = useCreditCardForm()

  const dataIsReady =
    bearerTokenResponse.isSuccess &&
    paymentIntentResponse.isSuccess &&
    fetchRestaurantV2Response.isSuccess

  React.useEffect(() => {
    track(AnalyticsEvents.SPI_CREATE_PAYMENT_INTENT_SUCCESS)
  }, [dataIsReady])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setPaymentLoading(true)
    setPaymentError(null)
    if (!spiSdk) {
      throw new Error('Failed to initialize payment.')
    }
    if (!dataIsReady) {
      throw new Error('Cannot submit form before required data is available')
    }

    payDepositSpi(
      {
        checkId: checkGuid,
        orderId: orderGuid,
        email: email,
        lineItems: [
          {
            amount: props.paymentAmount,
            label: 'Reservation payment'
          }
        ],
        paymentIntentId: paymentIntentResponse.data.id,
        restaurantId: restaurant.guid
      },
      spiSdk,
      {
        onConfirmPaymentIntentError() {
          track(AnalyticsEvents.SPI_CONFIRM_PAYMENT_INTENT_ERROR)
        },
        onConfirmPaymentIntentSuccess() {
          track(AnalyticsEvents.SPI_CONFIRM_PAYMENT_INTENT_SUCCESS)
        },
        onCreatePaymentMethodError() {
          track(AnalyticsEvents.SPI_CREATE_PAYMENT_METHOD_ERROR)
        },
        onCreatePaymentMethodSuccess() {
          track(AnalyticsEvents.SPI_CREATE_PAYMENT_METHOD_SUCCESS)
        },
        onUpdatePaymentIntentError() {
          track(AnalyticsEvents.SPI_UPDATE_PAYMENT_INTENT_ERROR)
        },
        onUpdatePaymentIntentSuccess() {
          track(AnalyticsEvents.SPI_UPDATE_PAYMENT_INTENT_SUCCESS)
        }
      }
    )
      .then(() => {
        onSuccess({
          amount: props.paymentAmount,
          // Hard-coded, becaus SPI only supports USD
          currency: Currency.USD,
          paymentProvider: 'SPI',
          selectedPaymentMethod: selectedPaymentMethod
        })
        setPaymentLoading(false)
      })
      .catch((err) => {
        if ('message' in err) {
          sentry.captureException(err, {
            captureContext: {
              extra: {
                restaurantGuid: restaurant.guid
              }
            }
          })
          setPaymentError(err.message)
        } else {
          setPaymentError('Something went wrong')
        }
        setPaymentLoading(false)
      })
  }

  if (
    paymentIntentResponse.isLoading ||
    bearerTokenResponse.isLoading ||
    fetchRestaurantV2Response.isLoading
  ) {
    return (
      <form id='paymentForm' onSubmit={handleSubmit}>
        <CreditCardFormDataPending />
      </form>
    )
  }

  return (
    <form id='paymentForm' onSubmit={handleSubmit}>
      {dataIsReady ? (
        <SpiPaymentElement
          pendingElement={<CreditCardFormDataPending />}
          onInitSuccessCallback={(e) => {
            setIsCcDataValid(e.content.isValid)
            setSelectedPaymentMethod(e.content.selectedPaymentMethod)
          }}
          onChange={(e) => {
            setSelectedPaymentMethod(e.content.selectedPaymentMethod)
            setIsCcDataValid(e.content.isValid)
          }}
          spiInitializationParams={{
            acceptAmex: Boolean(
              fetchRestaurantV2Response.data.creditCardConfig.amexAccepted
            ),
            merchantId: restaurant.guid,
            oauthToken: bearerTokenResponse.data,
            sessionSecret: paymentIntentResponse.data.sessionSecret
          }}
        />
      ) : null}
    </form>
  )
}
